import { X } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState, type KeyboardEvent } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import type { EmailRecipient } from "../types";

const EMAIL_PATTERN = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/;

/** Addresses typed or pasted, separated by commas, semicolons or spaces. */
export const parseAddresses = (text: string): string[] =>
  text
    .split(/[,;\s]+/)
    .map((part) => part.trim().replace(/^<|>$/g, "").toLowerCase())
    .filter((part) => EMAIL_PATTERN.test(part));

const recipientLabel = (recipient: EmailRecipient) =>
  recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email;

/**
 * Recipients as removable chips. An address is added with Enter, a comma or
 * on leaving the field; `suggestions` are the record's known addresses.
 */
export const RecipientsInput = ({
  id,
  label,
  value,
  onChange,
  suggestions = [],
}: {
  id: string;
  label: string;
  value: EmailRecipient[];
  onChange: (recipients: EmailRecipient[]) => void;
  suggestions?: EmailRecipient[];
}) => {
  const translate = useTranslate();
  const [text, setText] = useState("");
  const [isInvalid, setInvalid] = useState(false);
  const has = (email: string) => value.some((item) => item.email === email);

  const add = (recipients: EmailRecipient[]) => {
    const fresh = recipients.filter(
      (recipient, index) =>
        !has(recipient.email) &&
        recipients.findIndex((other) => other.email === recipient.email) ===
          index,
    );
    if (fresh.length) onChange([...value, ...fresh]);
  };

  const commit = () => {
    if (!text.trim()) return;
    const addresses = parseAddresses(text);
    if (!addresses.length) {
      setInvalid(true);
      return;
    }
    add(addresses.map((email) => ({ email })));
    setText("");
    setInvalid(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit();
    } else if (event.key === "Backspace" && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const remaining = suggestions.filter((suggestion) => !has(suggestion.email));

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-1 rounded-md border bg-background px-2 py-1 focus-within:ring-2 focus-within:ring-ring/50">
        {value.map((recipient) => (
          <Badge
            key={recipient.email}
            variant="secondary"
            className="gap-1 font-normal"
          >
            {recipientLabel(recipient)}
            <button
              type="button"
              className="cursor-pointer opacity-60 hover:opacity-100"
              aria-label={translate("crm.mail.compose.remove_recipient", {
                email: recipient.email,
              })}
              onClick={() =>
                onChange(value.filter((item) => item.email !== recipient.email))
              }
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
        <Input
          id={id}
          type="email"
          multiple
          className="h-8 min-w-48 flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setInvalid(false);
          }}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          aria-invalid={isInvalid}
        />
      </div>
      {isInvalid ? (
        <span className="text-xs text-destructive">
          {translate("crm.mail.errors.invalid_recipient")}
        </span>
      ) : null}
      {remaining.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {remaining.map((suggestion) => (
            <Button
              key={suggestion.email}
              type="button"
              variant="outline"
              size="sm"
              className="h-7 font-normal"
              onClick={() => add([suggestion])}
            >
              + {recipientLabel(suggestion)}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
